import { getProgramClosestToTimestamp, programIsLive, programIsUpcoming, programIsVod } from '@jwp/ott-common/src/utils/epg';
import type { EpgChannel, EpgProgram } from '@jwp/ott-common/types/epg';
import { type KeyboardEventHandler, type RefObject, useEffect, useRef, useState } from 'react';
import useInputMode from '@jwp/ott-ui-react/src/hooks/useInputMode';
import type { Virtualizer } from '@tanstack/react-virtual';
import { addDays } from 'date-fns';

import { CHANNEL_HEIGHT_WITH_SPACING } from './constants';

export type FocusState = { hasFocus: boolean; rowIndex: number; programId: string | undefined };

const useEpgKeyboardControl = ({
  channels,
  scrollViewRef,
  getColumnOffset,
  rowVirtualizer,
  columnVirtualizer,
  dayOffset,
  onProgramClick,
}: {
  channels: EpgChannel[];
  scrollViewRef: RefObject<HTMLDivElement | null>;
  getColumnOffset: (date: number) => number;
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
  columnVirtualizer: Virtualizer<HTMLDivElement, Element>;
  dayOffset: number;
  onProgramClick: (program: EpgProgram) => void;
}) => {
  const inputMode = useInputMode();
  const [focusState, setFocusState] = useState<FocusState>({
    hasFocus: false,
    rowIndex: 0,
    programId: undefined,
  });
  // keep the current program midpoint so the up/down navigation behaves more expected instead of using the current program midpoint
  const currentMidpoint = useRef(Date.now());
  const followLivePrograms = useRef(true);

  useEffect(() => {
    // toggle live following when the day offset changes
    followLivePrograms.current = dayOffset === 0;

    if (!channels[0]) return;

    // find the program in the "now" line using the day offsets
    const initialProgram = getProgramClosestToTimestamp(channels[0]?.programs, addDays(new Date(), dayOffset).getTime());

    if (initialProgram) {
      setFocusState((current) => {
        // program is still visible/included, don't change the active program
        if (channels[current.rowIndex]?.programs.some(({ id }) => id === current.programId)) return current;

        // reset row and the initial program
        return { ...current, rowIndex: 0, programId: initialProgram?.id };
      });
      currentMidpoint.current = new Date(initialProgram.startTime).getTime() + 15 * 1000;
    }
  }, [channels, dayOffset]);

  const handleFocus = () => {
    if (inputMode !== 'keyboard') return;
    const initialProgram = channels[0].programs.find(programIsLive);

    setFocusState((current) => ({ ...current, programId: current.programId || initialProgram?.id, hasFocus: true }));

    if (!currentMidpoint.current && initialProgram) {
      currentMidpoint.current = new Date(initialProgram.startTime).getTime() + 15 * 1000;
    }

    const channelOffset = focusState.rowIndex * CHANNEL_HEIGHT_WITH_SPACING;
    const scrollMargin = (columnVirtualizer.scrollRect?.width || 0) / 3;
    const programOffset = getColumnOffset(currentMidpoint.current) - scrollMargin;

    scrollViewRef.current?.scrollTo({ left: programOffset, top: channelOffset, behavior: 'smooth' });
  };
  const handleBlur = () => {
    // always blur
    setFocusState((current) => ({ ...current, hasFocus: false }));
  };

  const getNextProgram = (nextChannel: EpgChannel, currentProgram: EpgProgram | undefined) => {
    const liveProgram = nextChannel.programs.find(programIsLive);

    if (!currentProgram || (followLivePrograms.current && programIsLive(currentProgram))) return liveProgram;

    return getProgramClosestToTimestamp(nextChannel.programs, currentMidpoint.current) || liveProgram;
  };

  const handleKeyDown: KeyboardEventHandler = (event) => {
    if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown', 'Enter'].includes(event.key)) return;

    event.preventDefault();

    const { programId, rowIndex } = focusState;
    let nextProgram: EpgProgram | undefined;
    let nextRowIndex: number | undefined;
    let scrollToProgram = false;

    const programIndex = channels[rowIndex]?.programs.findIndex(({ id }) => id === programId);
    const currentProgram = channels[rowIndex]?.programs[programIndex] as EpgProgram | undefined;

    if (event.key === 'Enter') {
      if (currentProgram) onProgramClick(currentProgram);
      return;
    }

    if (event.key === 'Home') {
      if (!currentProgram) return;

      if (programIsUpcoming(currentProgram)) {
        // navigate to live program first
        nextProgram = channels[rowIndex].programs.find(programIsLive);
      } else {
        // first program
        nextProgram = channels[rowIndex].programs[0];
      }

      scrollToProgram = true;
    }

    if (event.key === 'End') {
      if (!currentProgram) return;

      if (programIsVod(currentProgram)) {
        // navigate to live program first
        nextProgram = channels[rowIndex].programs.find(programIsLive);
      } else {
        // last program
        nextProgram = channels[rowIndex].programs[channels[rowIndex].programs.length - 1];
      }

      scrollToProgram = true;
    }

    if (event.key === 'PageDown') {
      const visiblePrograms = Math.floor((rowVirtualizer.scrollRect?.height || 0) / CHANNEL_HEIGHT_WITH_SPACING) - 1;
      nextRowIndex = Math.min(channels.length - 1, rowIndex + visiblePrograms);
      nextProgram = getNextProgram(channels[nextRowIndex], currentProgram);
    }

    if (event.key === 'PageUp') {
      const visiblePrograms = Math.floor((rowVirtualizer.scrollRect?.height || 0) / CHANNEL_HEIGHT_WITH_SPACING);
      nextRowIndex = Math.max(0, rowIndex - visiblePrograms);
      nextProgram = getNextProgram(channels[nextRowIndex], currentProgram);
    }

    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      const offset = event.key === 'ArrowLeft' ? -1 : 1;
      nextProgram = channels[rowIndex]?.programs[programIndex + offset] as EpgProgram | undefined;
      scrollToProgram = true;
    }

    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      const offset = event.key === 'ArrowUp' ? -1 : 1;
      const toRowIndex = Math.max(0, Math.min(channels.length - 1, rowIndex + offset));

      if (rowIndex !== toRowIndex) {
        nextRowIndex = toRowIndex;
        nextProgram = getNextProgram(channels[nextRowIndex], currentProgram);
      }
    }

    if (nextProgram || nextRowIndex) {
      setFocusState((current) => ({ ...current, programId: nextProgram?.id || current.programId, rowIndex: nextRowIndex ?? current.rowIndex }));
    }

    if (nextProgram && scrollToProgram) {
      currentMidpoint.current = new Date(nextProgram.startTime).getTime() + 30 * 1000;
      followLivePrograms.current = programIsLive(nextProgram);
    }

    const scrollToRow = nextRowIndex ?? rowIndex;
    const scrollMargin = (columnVirtualizer.scrollRect?.width || 0) / 3;

    const scrollToX = getColumnOffset(currentMidpoint.current) - scrollMargin;
    let scrollToY: number | undefined;

    if (scrollToRow !== undefined) {
      const channelOffset = scrollToRow * CHANNEL_HEIGHT_WITH_SPACING;
      const currentOffset = rowVirtualizer.scrollOffset || 0;
      const scrollHeight = (rowVirtualizer.scrollRect?.height || 0) - rowVirtualizer.options.paddingStart;

      if (channelOffset - CHANNEL_HEIGHT_WITH_SPACING < currentOffset) {
        scrollToY = channelOffset - CHANNEL_HEIGHT_WITH_SPACING;
      } else if (channelOffset + CHANNEL_HEIGHT_WITH_SPACING * 2 > currentOffset + scrollHeight) {
        scrollToY = channelOffset + CHANNEL_HEIGHT_WITH_SPACING * 2 - scrollHeight;
      }
    }

    if (scrollToY !== undefined || scrollToX !== undefined) {
      scrollViewRef.current?.scrollTo({ left: scrollToX, top: scrollToY, behavior: 'smooth' });
    }
  };

  return {
    focusState,
    handleBlur,
    handleFocus,
    handleKeyDown,
  };
};

export default useEpgKeyboardControl;
